import { useContext, useMemo } from 'react';

import type { AuthContextType } from '@/contexts/AuthContextDefinition';
import { AuthContext } from '@/contexts/AuthContextDefinition';
import { isDevMockAuth } from '@/utils/authCheck';

/**
 * Local-stack-only UI mock ("Sarah"). Keeping this behind the same guard as
 * BaseService prevents the shell from displaying an authenticated user while
 * data services correctly reject the fake session against hosted Supabase.
 * Enable with: localStorage.setItem('ENABLE_MOCK_AUTH_UI', 'true').
 */
const _DEV_MOCK_USER: any = {
  // This override is local-stack-only, so it must match supabase/seed.sql.
  id: import.meta.env.VITE_DEV_USER_ID || 'd6b5e688-66f8-4b77-a8f8-b8d8c8d8e8f8',
  email: 'test@example.com',
  user_metadata: { full_name: 'Sarah Chen' },
  aud: 'authenticated',
  created_at: '2026-01-01T00:00:00.000Z',
  app_metadata: {},
  is_anonymous: false,
};

const _DEV_NOOP_SIGN_IN = async () => ({ error: null, data: { session: null, user: null } as any });
const _DEV_NOOP_SIGN_UP = async () => ({ error: null, data: { session: null, user: null } as any });
const _DEV_NOOP_SIGN_OUT = async () => {};
const _DEV_NOOP_RESET_PW = async () => ({ error: null, data: { user: null } });
const _DEV_NOOP_RESEND_SIGNUP = async () => ({ error: null });
const _DEV_NOOP_UPDATE_PW = async () => ({ error: null, data: { user: null } });
const _DEV_NOOP_OAUTH = async () => ({ error: null, data: null });
const _DEV_NOOP_REFRESH = async () => {};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  const devOverride = useMemo<AuthContextType | null>(() => {
    if (localStorage.getItem('ENABLE_MOCK_AUTH_UI') !== 'true') return null;
    if (!isDevMockAuth()) return null;
    return {
      user: _DEV_MOCK_USER,
      session: {
        access_token: 'mock-token',
        refresh_token: 'mock-refresh',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer',
        user: _DEV_MOCK_USER,
      },
      loading: false,
      error: null,
      signIn: _DEV_NOOP_SIGN_IN,
      signUp: _DEV_NOOP_SIGN_UP,
      signOut: _DEV_NOOP_SIGN_OUT,
      resetPasswordForEmail: _DEV_NOOP_RESET_PW,
      resendSignupConfirmation: _DEV_NOOP_RESEND_SIGNUP,
      updatePassword: _DEV_NOOP_UPDATE_PW,
      signInWithGoogle: _DEV_NOOP_OAUTH,
      signInWithApple: _DEV_NOOP_OAUTH,
      signInWithToken: _DEV_NOOP_OAUTH,
      refreshUser: _DEV_NOOP_REFRESH,
    };
  }, []);

  if (devOverride) {
    if (context && (context as AuthContextType).session?.access_token) {
      return context as AuthContextType;
    }
    return devOverride;
  }

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context as AuthContextType;
}
